import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

interface UseChatDropzoneOptions {
  containerRef: RefObject<HTMLDivElement | null>;
  onDrop: (files: File[], skippedDirectoryCount: number) => void;
  enabled: boolean;
}

/**
 * 判断本次拖拽是否携带"外部文件"。
 * 内部节点（例如 tiptap 的 mention chip 拖动）只会带 text/html 等 MIME，
 * 不会带 Files；只有从系统/浏览器外拖入文件时 dataTransfer.types 才会包含 Files。
 */
function dragHasExternalFiles(e: DragEvent): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return false;
  // DataTransfer.types 在不同浏览器里是 DOMStringList 或 string[]，统一遍历判断。
  for (let i = 0; i < types.length; i += 1) {
    if (types[i] === 'Files') return true;
  }
  return false;
}

export interface ExtractFilesResult {
  files: File[];
  skippedDirectoryCount: number;
}

/**
 * 从 DataTransfer 中提取文件，过滤掉文件夹。
 *
 * 优先使用 `dataTransfer.items` + `webkitGetAsEntry()` 检测 entry 类型；
 * fallback：浏览器没给 items 时按 `file.size === 0 && file.type === ''` 兜底过滤。
 */
export function extractFilesFromDataTransfer(dt: DataTransfer): ExtractFilesResult {
  const files: File[] = [];
  let skippedDirectoryCount = 0;

  // 优先走 items + webkitGetAsEntry
  if (dt.items && dt.items.length > 0) {
    for (let i = 0; i < dt.items.length; i += 1) {
      const item = dt.items[i];
      if (item.kind !== 'file') continue;

      const entry = item.webkitGetAsEntry?.();
      if (entry) {
        if (entry.isDirectory) {
          skippedDirectoryCount += 1;
          continue;
        }
        // isFile === true
        const file = item.getAsFile();
        if (file) files.push(file);
      } else {
        // webkitGetAsEntry 不可用，fallback 到 getAsFile + heuristic
        const file = item.getAsFile();
        if (!file) continue;
        if (file.size === 0 && file.type === '') {
          // 大概率是文件夹
          skippedDirectoryCount += 1;
        } else {
          files.push(file);
        }
      }
    }
    return { files, skippedDirectoryCount };
  }

  // fallback：只有 dt.files（极少见）
  const rawFiles = Array.from(dt.files ?? []);
  for (const file of rawFiles) {
    if (file.size === 0 && file.type === '') {
      skippedDirectoryCount += 1;
    } else {
      files.push(file);
    }
  }
  return { files, skippedDirectoryCount };
}

/**
 * 在指定容器上挂载全域拖拽 + 粘贴文件监听。
 * - 拖拽只对"携带外部文件"的事件生效，避免编辑器内部 mention chip 等 DOM
 *   节点拖动时也触发"释放以上传文件"提示。
 * - 粘贴仅拦截含文件的事件，纯文本粘贴不受影响。
 * - 文件夹会被静默过滤，通过 onDrop 的 skippedDirectoryCount 参数通知调用方。
 */
export function useChatDropzone({ containerRef, onDrop, enabled }: UseChatDropzoneOptions) {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounterRef = useRef(0);
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;

  const resetDrag = useCallback(() => {
    dragCounterRef.current = 0;
    setIsDraggingOver(false);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) {
      resetDrag();
      return;
    }

    const handleDragEnter = (e: DragEvent) => {
      if (!dragHasExternalFiles(e)) return;
      e.preventDefault();
      dragCounterRef.current += 1;
      if (dragCounterRef.current === 1) setIsDraggingOver(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      if (dragCounterRef.current === 0) return;
      e.preventDefault();
      dragCounterRef.current -= 1;
      if (dragCounterRef.current === 0) setIsDraggingOver(false);
    };

    const handleDragOver = (e: DragEvent) => {
      if (!dragHasExternalFiles(e)) return;
      e.preventDefault();
    };

    const handleDrop = (e: DragEvent) => {
      if (!dragHasExternalFiles(e)) {
        // 让 tiptap 等内部 drop 处理器正常工作，不要默认拦截。
        resetDrag();
        return;
      }
      e.preventDefault();
      resetDrag();
      const dt = e.dataTransfer;
      if (!dt) return;
      const { files, skippedDirectoryCount } = extractFilesFromDataTransfer(dt);
      if (files.length > 0 || skippedDirectoryCount > 0) {
        onDropRef.current(files, skippedDirectoryCount);
      }
    };

    const handlePaste = (e: ClipboardEvent) => {
      const pasteFiles = Array.from(e.clipboardData?.files ?? []);
      if (!pasteFiles.length) return;
      e.preventDefault();
      // 粘贴不会有文件夹，直接传 0
      onDropRef.current(pasteFiles, 0);
    };

    container.addEventListener('dragenter', handleDragEnter);
    container.addEventListener('dragleave', handleDragLeave);
    container.addEventListener('dragover', handleDragOver);
    container.addEventListener('drop', handleDrop);
    container.addEventListener('paste', handlePaste);

    return () => {
      container.removeEventListener('dragenter', handleDragEnter);
      container.removeEventListener('dragleave', handleDragLeave);
      container.removeEventListener('dragover', handleDragOver);
      container.removeEventListener('drop', handleDrop);
      container.removeEventListener('paste', handlePaste);
      resetDrag();
    };
  }, [containerRef, enabled, resetDrag]);

  return { isDraggingOver };
}
